import { SCREEN_PATTERNS } from '@base/shared';
import { useTranslation } from 'react-i18next';
import { StateView, type StateKind } from '../../components/patterns/index.ts';
import { TabPanel, Tabs, useTabInUrl } from '../../components/Tabs.tsx';
import { PageHeader, Panel } from '../../components/ui.tsx';
import { localized } from '../../i18n/index.ts';
import { PatternExample } from './pattern-examples.tsx';

const IDS = SCREEN_PATTERNS.map((p) => p.id);
const STATES: StateKind[] = ['empty', 'loading', 'error', 'denied'];

/**
 * Configuração › Padrões de tela (ADR 0128, fase 4): a vitrine dos padrões, com dados de exemplo,
 * no layout e no acabamento que valem agora. É o que a IA usa a cada tela nova e o que o estúdio
 * mostra na prévia (fase 5).
 */
export function PatternsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useTabInUrl(IDS, IDS[0]!);
  const pattern = SCREEN_PATTERNS.find((p) => p.id === tab)!;
  return (
    <>
      <PageHeader title={t('nav.patterns')} description={t('patternsPage.description')} />
      <div className="flex flex-col gap-4">
        <Tabs
          items={SCREEN_PATTERNS.map((p) => ({ id: p.id, label: localized(p.name) }))}
          value={tab}
          onChange={setTab}
          label={t('nav.patterns')}
          idPrefix="patterns"
        />
        <TabPanel id={tab} idPrefix="patterns">
          <div className="flex flex-col gap-4">
            <Panel>
              <dl className="grid gap-3 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">
                <dt className="text-muted">{t('patternsPage.whenToUse')}</dt>
                <dd>{localized(pattern.whenToUse)}</dd>
                <dt className="text-muted">{t('patternsPage.pieces')}</dt>
                <dd className="flex flex-wrap gap-1.5">
                  {pattern.pieces.map((p) => (
                    <code key={p} className="rounded bg-paper px-1.5 py-0.5 font-mono text-xs">
                      {p}
                    </code>
                  ))}
                </dd>
              </dl>
            </Panel>
            <section aria-label={t('patternsPage.example')} className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold">{t('patternsPage.example')}</h2>
              <PatternExample id={tab} />
            </section>
            {tab !== 'login' && tab !== 'document' && (
              <section className="flex flex-col gap-2">
                <h2 className="text-sm font-semibold">{t('patternsPage.states')}</h2>
                <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {STATES.map((kind) => (
                    <li key={kind} className="ui-panel flex flex-col rounded-lg bg-sheet">
                      <span className="border-b border-rule px-4 py-2 text-xs text-muted">
                        {t(`patternsPage.stateLabel.${kind}`)}
                      </span>
                      <StateView kind={kind} compact />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </TabPanel>
      </div>
    </>
  );
}
